/**
 * Block 10 (2026-05-27): FOP annual-limit dashboard widget.
 *
 * Multi-org aggregation для tenants з ≥1 FOP-org:
 * - Loads all FOP-orgs (filter is_fop)
 * - Bulk endpoint → sorted checks + summary counts
 * - Default filter: non-OK rows only (show OK rows toggle)
 * - Click row → drill-down /fop/reports?org=X (state-prefilled)
 *
 * Empty state коли tenant без FOP-orgs — без bulk API call.
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { orgs } from '@/api/catalogs';
import {
  fopReports,
  type AnnualLimitCheck,
  type WarningLevel,
} from '@/api/fop_reports';
import {
  WARNING_COLOR,
  WARNING_LABEL_UA,
} from '@/shared/warning_colors';
import LimitCheckBadge from '@/shared/components/LimitCheckBadge';
import DashboardWidget from '../DashboardWidget';

export default function FopLimitsWidget() {
  const navigate = useNavigate();
  const [showAll, setShowAll] = useState(false);
  const currentYear = new Date().getFullYear();

  const orgListQ = useQuery({
    queryKey: ['organizations', 'fop-widget'],
    queryFn: () => orgs.list({ limit: 200 }),
  });
  const fopOrgIds = useMemo(
    () => (orgListQ.data?.items ?? []).filter((o) => o.is_fop).map((o) => o.id),
    [orgListQ.data],
  );

  const bulkQ = useQuery({
    queryKey: ['fop-limits-bulk', fopOrgIds, currentYear],
    queryFn: () => fopReports.annualLimitCheckBulk(fopOrgIds, currentYear),
    enabled: fopOrgIds.length > 0,
    staleTime: 60_000, // 1 хв — limits не змінюються часто
  });

  const checks: AnnualLimitCheck[] = bulkQ.data?.checks ?? [];
  const summary = bulkQ.data?.summary;
  const visibleChecks = showAll
    ? checks
    : checks.filter((c) => c.warning_level !== 'OK');

  const loading = orgListQ.isLoading || (fopOrgIds.length > 0 && bulkQ.isLoading);
  const isEmpty = !loading && fopOrgIds.length === 0;

  return (
    <DashboardWidget
      title="Ліміти єдинників"
      icon="📊"
      loading={loading}
      isEmpty={isEmpty}
      emptyMessage="Немає організацій-єдинників у вашому тенанті"
      actions={
        checks.length > 0 ? (
          <label style={{ fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: 4 }}>
            <input
              type="checkbox"
              checked={showAll}
              onChange={(e) => setShowAll(e.target.checked)}
            />
            Показати всі ({checks.length})
          </label>
        ) : undefined
      }
    >
      {summary && (
        <div
          data-testid="fop-limits-summary"
          style={{
            display: 'flex',
            gap: 8,
            flexWrap: 'wrap',
            marginBottom: '0.5rem',
            fontSize: '0.82rem',
          }}
        >
          {(['CRITICAL', 'WARNING', 'INFO', 'OK'] as WarningLevel[]).map((lvl) => {
            const key = `${lvl.toLowerCase()}_count` as
              | 'critical_count' | 'warning_count' | 'info_count' | 'ok_count';
            const count = summary[key];
            if (count === 0) return null;
            // Якщо showAll=false і це OK — позначаємо «приховано».
            const hidden = !showAll && lvl === 'OK';
            return (
              <span
                key={lvl}
                style={{
                  padding: '2px 8px',
                  borderRadius: 12,
                  background: WARNING_COLOR[lvl] + '22',
                  color: WARNING_COLOR[lvl],
                  fontWeight: 500,
                }}
              >
                {count} {WARNING_LABEL_UA[lvl]}
                {hidden ? ' (приховано)' : ''}
              </span>
            );
          })}
        </div>
      )}

      <div
        data-testid="fop-limits-list"
        style={{ display: 'flex', flexDirection: 'column', gap: 6 }}
      >
        {visibleChecks.map((check) => (
          <LimitCheckBadge
            key={check.organization_id}
            data={check}
            mode="compact"
            onClick={() =>
              navigate(
                `/fop/reports?org=${check.organization_id}&tab=limit`,
              )
            }
          />
        ))}
        {visibleChecks.length === 0 && checks.length > 0 && !showAll && (
          <p style={{ color: '#10b981', fontStyle: 'italic' }}>
            ✓ Усі {checks.length} ФОП у нормі — без попереджень
          </p>
        )}
      </div>
    </DashboardWidget>
  );
}
